import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { courseAPI, categoryAPI } from "@/lib/api";
import api from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { Progress } from "@/components/ui/progress";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Save, 
  ArrowLeft, 
  Loader2,
  Eye,
  EyeOff,
  Edit3,
  BookOpen,
  Info,
  CheckCircle,
  Maximize2,
  X,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Layers
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { HtmlContentWithPdfSlides } from "@/components/editor/PdfSlidesRenderer";

export default function CourseEditorPage() {
  const { workspaceId, courseId } = useParams();
  const navigate = useNavigate();
  const { canManage, user } = useAuth();
  const isNew = !courseId;
  const contentRef = useRef(null);
  
  // Check if we're in view-only mode (URL ends with /view)
  const isViewMode = window.location.pathname.endsWith('/view');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(isViewMode ? "preview" : (canManage ? "edit" : "preview"));
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    sections: [],
    chapter: "",
    category_id: "",
    order: 0,
    attachments: [],
  });

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await contentRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
      toast.error("Impossible d'activer le mode plein écran");
    }
  };

  useEffect(() => {
    if (!isNew) {
      loadCourse();
    }
    // Load categories
    categoryAPI.list(workspaceId).then(res => setCategoriesList(res.data)).catch(() => {});
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const response = await courseAPI.get(workspaceId, courseId);
      setFormData({
        ...response.data,
        sections: response.data.sections || []
      });
      
      // Load progress for candidates
      if (!canManage) {
        try {
          const progressRes = await api.get(`/workspaces/${workspaceId}/courses/${courseId}/progress`);
          setProgress(progressRes.data.progress_percent || 0);
          setCompleted(progressRes.data.completed || false);
        } catch (e) {
          console.log("No progress yet");
        }
      }
    } catch (error) {
      console.error("Failed to load course:", error);
      toast.error("Erreur lors du chargement du cours");
      navigate(`/workspace/${workspaceId}/courses`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }

    setSaving(true);

    try {
      if (isNew) {
        await courseAPI.create(workspaceId, formData);
        toast.success("Cours créé avec succès");
      } else {
        await courseAPI.update(workspaceId, courseId, formData);
        toast.success("Cours mis à jour");
      }
      navigate(`/workspace/${workspaceId}/courses`);
    } catch (error) {
      console.error("Failed to save course:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Gestion des sections
  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: `Section ${formData.sections.length + 1}`,
      content: "",
      order: formData.sections.length,
      is_visible: true
    };
    setFormData({
      ...formData,
      sections: [...formData.sections, newSection]
    });
    setActiveSection(newSection.id);
  };

  const updateSection = (sectionId, field, value) => {
    setFormData({
      ...formData,
      sections: formData.sections.map(s => 
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    });
  };

  const toggleSectionVisibility = (sectionId) => {
    setFormData({
      ...formData,
      sections: formData.sections.map(s => 
        s.id === sectionId ? { ...s, is_visible: !s.is_visible } : s
      )
    });
  };

  const deleteSection = (sectionId) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter(s => s.id !== sectionId)
    });
    if (activeSection === sectionId) {
      setActiveSection(null);
    }
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    const newSections = [...formData.sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    // Update order
    newSections.forEach((s, i) => s.order = i);
    setFormData({ ...formData, sections: newSections });
  };

  const moveSectionDown = (index) => {
    if (index === formData.sections.length - 1) return;
    const newSections = [...formData.sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    // Update order
    newSections.forEach((s, i) => s.order = i);
    setFormData({ ...formData, sections: newSections });
  };

  const handleContentChange = (html) => {
    setFormData({ ...formData, content: html });
  };

  const markAsComplete = async () => {
    try {
      await api.put(`/workspaces/${workspaceId}/courses/${courseId}/progress`, {
        course_id: courseId,
        completed: true,
        progress_percent: 100
      });
      setCompleted(true);
      setProgress(100);
      toast.success("Cours marqué comme terminé !");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  // View mode for candidates
  if (!canManage && !isNew) {
    return (
      <WorkspaceLayout>
        <div className="page-container animate-fade-in">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/workspace/${workspaceId}/courses`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold tracking-tight">{formData.title}</h1>
              {formData.chapter && (
                <p className="text-muted-foreground">{formData.chapter}</p>
              )}
            </div>
            <Button 
              variant="outline" 
              size="icon"
              onClick={toggleFullscreen}
              title="Plein écran"
              data-testid="fullscreen-btn"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            {!completed && (
              <Button onClick={markAsComplete} className="gap-2">
                <CheckCircle className="h-4 w-4" />
                Marquer comme terminé
              </Button>
            )}
            {completed && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Terminé</span>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Progression</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {formData.description && (
            <p className="text-muted-foreground mb-6">{formData.description}</p>
          )}

          {/* Affichage du contenu principal si présent */}
          {formData.content && (
            <Card className="mb-6">
              <CardContent className="p-0">
                <div 
                  ref={contentRef}
                  className={`prose prose-slate max-w-none p-6 ${isFullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : ''}`}
                >
                  {isFullscreen && (
                    <div className="sticky top-0 right-0 flex justify-between items-center mb-4 pb-4 border-b bg-white">
                      <h2 className="text-xl font-semibold">{formData.title}</h2>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={toggleFullscreen}
                        title="Quitter le plein écran"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <HtmlContentWithPdfSlides html={formData.content} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Affichage des sections */}
          {formData.sections && formData.sections.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Sections du cours
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {formData.sections
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((section, index) => (
                    <AccordionItem key={section.id} value={section.id} className="border rounded-lg">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <span className="flex items-center gap-2">
                          <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-1 rounded">
                            {index + 1}
                          </span>
                          {section.title}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <HtmlContentWithPdfSlides 
                          html={section.content || "<p>Aucun contenu</p>"}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
              </Accordion>
            </div>
          )}

          {/* Message si aucun contenu */}
          {!formData.content && (!formData.sections || formData.sections.length === 0) && (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                Aucun contenu disponible pour ce cours
              </CardContent>
            </Card>
          )}
        </div>
      </WorkspaceLayout>
    );
  }

  // Edit mode for trainers/admins
  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/courses`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-semibold tracking-tight">
              {isNew ? "Nouveau cours" : "Modifier le cours"}
            </h1>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="gap-2" data-testid="save-course-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Titre du cours *</Label>
                <Input
                  id="title"
                  placeholder="Ex: Introduction à Docker"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="course-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Décrivez brièvement le contenu de ce cours..."
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="course-description-input"
                />
              </div>

              {/* Introduction/Contenu principal (optionnel) */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Introduction (optionnel)
                    </span>
                    {activeTab === "preview" && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={toggleFullscreen}
                        className="gap-2"
                        data-testid="fullscreen-preview-btn"
                      >
                        <Maximize2 className="h-4 w-4" />
                        Plein écran
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-4">
                      <TabsTrigger value="edit" className="gap-2">
                        <Edit3 className="h-4 w-4" />
                        Édition
                      </TabsTrigger>
                      <TabsTrigger value="preview" className="gap-2">
                        <Eye className="h-4 w-4" />
                        Aperçu
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="edit">
                      <RichTextEditor
                        content={formData.content || ""}
                        onChange={handleContentChange}
                        placeholder="Contenu d'introduction du cours (optionnel)..."
                      />
                    </TabsContent>
                    
                    <TabsContent value="preview">
                      <div 
                        ref={contentRef}
                        className={`prose prose-slate max-w-none min-h-[200px] p-4 rounded-lg border bg-muted/20 ${isFullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : ''}`}
                      >
                        {isFullscreen && (
                          <div className="sticky top-0 right-0 flex justify-between items-center mb-4 pb-4 border-b bg-white">
                            <h2 className="text-xl font-semibold">{formData.title || "Aperçu du cours"}</h2>
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={toggleFullscreen}
                              title="Quitter le plein écran"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        <HtmlContentWithPdfSlides html={formData.content || "<p class='text-muted-foreground'>Aucun contenu d'introduction</p>"} />
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Sections du cours */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Layers className="h-5 w-5" />
                      Sections du cours
                    </span>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={addSection}
                      className="gap-2"
                      data-testid="add-section-btn"
                    >
                      <Plus className="h-4 w-4" />
                      Ajouter une section
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {formData.sections.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                      <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="mb-4">Aucune section pour le moment</p>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={addSection}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter la première section
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.sections.map((section, index) => (
                        <div 
                          key={section.id}
                          className={cn(
                            "border rounded-lg transition-all",
                            activeSection === section.id ? "ring-2 ring-primary" : "",
                            section.is_visible === false && "opacity-60 border-yellow-300 bg-yellow-50/50"
                          )}
                        >
                          {/* Section Header */}
                          <div 
                            className="flex items-center gap-2 p-3 bg-muted/50 cursor-pointer"
                            onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                          >
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <span className={cn(
                              "text-xs font-bold px-2 py-1 rounded",
                              section.is_visible === false 
                                ? "bg-yellow-200 text-yellow-800" 
                                : "bg-primary text-primary-foreground"
                            )}>
                              {index + 1}
                            </span>
                            <Input
                              value={section.title}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateSection(section.id, "title", e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 h-8 font-medium"
                              placeholder="Titre de la section"
                              data-testid={`section-title-${index}`}
                            />
                            {section.is_visible === false && (
                              <span className="text-xs text-yellow-600 font-medium">Cachée</span>
                            )}
                            <div className="flex items-center gap-1">
                              {/* Visibility toggle */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSectionVisibility(section.id);
                                }}
                                title={section.is_visible === false ? "Rendre visible" : "Cacher"}
                                data-testid={`toggle-section-visibility-${index}`}
                              >
                                {section.is_visible === false ? (
                                  <EyeOff className="h-4 w-4 text-yellow-600" />
                                ) : (
                                  <Eye className="h-4 w-4 text-green-600" />
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveSectionUp(index);
                                }}
                                disabled={index === 0}
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveSectionDown(index);
                                }}
                                disabled={index === formData.sections.length - 1}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSection(section.id);
                                }}
                                data-testid={`delete-section-${index}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Section Content */}
                          {activeSection === section.id && (
                            <div className="p-4 border-t">
                              <RichTextEditor
                                content={section.content || ""}
                                onChange={(html) => updateSection(section.id, "content", html)}
                                placeholder={`Contenu de la section "${section.title}"...`}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Organisation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="chapter">Chapitre</Label>
                    <Input
                      id="chapter"
                      placeholder="Ex: Module 1"
                      value={formData.chapter || ""}
                      onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                      data-testid="course-chapter-input"
                    />
                  </div>
                  {categoriesList.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="category">Catégorie</Label>
                      <select
                        id="category"
                        value={formData.category_id || ""}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="course-category-select"
                      >
                        <option value="">Sans catégorie</option>
                        {categoriesList.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="order">Ordre d'affichage</Label>
                    <Input
                      id="order"
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      data-testid="course-order-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Fonctionnalités
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Mise en forme riche</li>
                    <li>• Images et vidéos YouTube</li>
                    <li>• Google Slides protégé</li>
                    <li>• Blocs HTML personnalisés</li>
                    <li>• Plusieurs sections par cours</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Résumé des sections */}
              {formData.sections.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Sections ({formData.sections.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="text-sm space-y-2">
                      {formData.sections.map((section, index) => (
                        <li 
                          key={section.id}
                          className={cn(
                            "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
                            activeSection === section.id && "bg-primary/10",
                            section.is_visible === false && "opacity-50"
                          )}
                          onClick={() => setActiveSection(section.id)}
                        >
                          <span className={cn(
                            "text-xs font-bold w-6 h-6 flex items-center justify-center rounded",
                            section.is_visible === false 
                              ? "bg-yellow-200 text-yellow-800" 
                              : "bg-primary/20 text-primary"
                          )}>
                            {index + 1}
                          </span>
                          <span className="truncate flex-1">{section.title || "Sans titre"}</span>
                          {section.is_visible === false ? (
                            <EyeOff className="h-4 w-4 text-yellow-600" />
                          ) : section.content ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <div className="h-4 w-4 border-2 rounded-full" />
                          )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </form>
      </div>
    </WorkspaceLayout>
  );
}
